<template>
	<div
	:id="useAgencyLocation ? 'map-static-wrapper' : 'map-wrapper'"
	:class="{'mobile': getMobileAppState, 'non-authenticated-route': nonAuthenticatedRoute}"/>
</template>

<script setup>
import Clusters from '@/assets/googleMaps/markerclusterer'
import mapIcon from '@/assets/images/map-pin-agency.png'

/* Vue & Assets */
import { ref, watch, onMounted, toRaw, computed } from 'vue'
import { mapApiLoader } from '@/assets/localPlugins/gMaps'
import { tooltipBuilder } from '@/components/features/agencies/Tooltip.js'
import { useRoute } from 'vue-router'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { nonAuthenticatedRoutes } from '@/router/helpers/validations'

const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['markerClicked', 'initalPositionMoved', 'mapError'])

const props = defineProps({
	mapOptions: Object,
	locations: Array,
	elementsList: Array,
	useMyCurrentLocation: Boolean,
	useAgencyLocation: Boolean,
	agencyLocation: Object,
	isStaticMap: Boolean
})

const firstMapRender = ref(true)
const markers = ref([])
const map = ref('')
const markerCluster = ref('')
const googleAPI = ref('')
const infowindow = ref('')
const currentLocation = ref({ lat: '', lng: '' })
const currentRoute = useRoute()
const zoomOut = ref(false)

/* Controls if current route is non authenticated */
const nonAuthenticatedRoute = computed(() => {
    return nonAuthenticatedRoutes().has(currentRoute.path)
})

/* inits the map */
const initMap = () => {
	const container = document.getElementById(props.useAgencyLocation ? 'map-static-wrapper' : 'map-wrapper')
	/* due to the way containers are set up in the project,
	* we need to get the width from the window and then
	* remove the 300px of the menu
	*/
	container.style.width = getMobileAppState.value ? `${window.innerWidth}px` : `${window.innerWidth - 300}px`
	// if (getMobileAppState.value) container.style.height = `${window.innerHeight}px`
    console.log(googleAPI.value.maps, 'inside init map')
	map.value = new googleAPI.value.maps.Map(container, props.mapOptions)
	/* place the locations on map */
    if (firstMapRender.value && props.locations.length === 1) {
        setTimeout(() => {
            placeLocations()
        }, 10)
    } else {
        placeLocations()
    }
	/* run when the window is resized */
	window.onresize = () => {
		/* we update the size of the container, and the map will adjust on his own */
		container.style.width = getMobileAppState.value ? `${window.innerWidth}px` : `${window.innerWidth - 300}px`
		// if (getMobileAppState.value) container.style.height = `${window.innerHeight}px`
	}

	map.value.addListener('center_changed', () => { emit('initalPositionMoved') })
}

const placeLocations = () => {
	/* before we place locations, we clear all the previous ones */
	clearMarkers()

	if (!props.locations.length) return

	/* create the markers */
	markers.value = props.locations.map((location, i) => {
		const latlng = new googleAPI.value.maps.LatLng(parseFloat(location.lat, 10), parseFloat(location.lng, 10))

		const icon = mapIcon

		const marker = new googleAPI.value.maps.Marker({
			position: latlng,
			icon: icon
		})

		marker.addListener('click', ev => {
			/* Removing previous infoWindow and it's event binding */
			if (infowindow.value) {
				if (document.getElementById('agency-tooltip-wrapper')) {
					document.getElementById('agency-tooltip-wrapper').removeEventListener('click', () => {
						emit('markerClicked', props.elementsList.find(el => el.id === location.id))
					})
				}
				infowindow.value.close()
			}

			infowindow.value = new googleAPI.value.maps.InfoWindow({
				content: tooltipBuilder(props.elementsList.find(el => el.id === location.id)),
				maxWidth: '152px'
			})

			infowindow.value.open(map.value, marker)

			setTimeout(() => {
				document.getElementById('agency-tooltip-wrapper').addEventListener('click', () => {
					emit('markerClicked', props.elementsList.find(el => el.id === location.id))
				})
			}, 10)
		})

		return marker
	})

	/* add the marker cluster to manage it */
	markerCluster.value = new Clusters.MarkerClusterer(
		map.value,
		markers.value,
		{ imagePath: 'images/googleMaps/m' }
	)

	if (props.useAgencyLocation) {
		const location = new googleAPI.value.maps.LatLng(parseFloat(props.agencyLocation.latitude, 10), parseFloat(props.agencyLocation.longitude, 10))
		map.value.panTo(location)
		map.value.setZoom(17)
	} else if (props.locations.length === 1) {
		const location = new googleAPI.value.maps.LatLng(parseFloat(props.locations[0].lat, 10), parseFloat(props.locations[0].lng, 10))
		map.value.panTo(location)
		map.value.setZoom(17)
	} else if (zoomOut.value) {
        map.value.panTo(getMobileAppState.value ? { lat: -12.041292, lng: 17.032197 } : { lat: -11.841292, lng: 16.432197 })
        map.value.setZoom(getMobileAppState.value ? 5 : 6)
    }
}

const clearMarkers = () => {
	markers.value.forEach(marker => {
		toRaw(marker).setMap(null) // This removes the marker from the array but on the map but it still can been seen and clicked
	})

	markers.value = []
	if (markerCluster.value) markerCluster.value.clearMarkers()
}

const loader = async () => {
	try {
		const google = await mapApiLoader()
		googleAPI.value = google
        setTimeout(() => {
            initMap()
        })
	} catch (error) {
        console.log(error)
		emit('mapError')
	}
}

watch(() => props.locations, (newVal, oldVal) => {
    console.log('here')
    if (newVal.length !== oldVal.length) zoomOut.value = true
    placeLocations()
})

watch(() => props.useMyCurrentLocation, () => {
	if (props.useMyCurrentLocation) {
		const location = new googleAPI.value.maps.LatLng(parseFloat(currentLocation.value.lat, 10), parseFloat(currentLocation.value.lng, 10))

		map.value.panTo(location)
	}
})

onMounted(() => {
	navigator.geolocation.getCurrentPosition(function (position) {
        currentLocation.value.lat = position.coords.latitude
        currentLocation.value.lng = position.coords.longitude
    })

    loader()
})
</script>

<style lang="scss">
/* NOTE */
/* this styles are hardcoded for the agencies
	if there is a need for the map somnehwere
	else , we will need to make different styles
*/
#map-wrapper {
	max-width: calc(100% - 48px);
	height: calc(100% - 240px); // header size + margin bottom
	left: 50%;
	transform: translateX(-50%);
	z-index: -1;
	position:absolute !important;
	border-radius: 16px;

	&.mobile {
		max-width: 100%;
		height: 100%;
	}

	&.non-authenticated-route {
		&:not(.mobile) {
			width: 100% !important;
			max-width: 100%;
			height: calc(100% - 190px);
		}
	}
}

#map-static-wrapper {
	max-width: 1515px;
    height: calc(100% - 24px);
    max-height: 600px;
    left:50%;
    transform:translateX(-50%);
    z-index:-1;
	position:absolute !important;
	border-radius: 16px;
}

.gm-style {
	.gm-fullscreen-control {
		width: 56px !important;
		height: 56px !important;
		position: absolute !important;
		right: 30px !important;
		bottom: 100px !important;
		border-radius: 50% !important;
		top:unset !important;
		margin: 0 !important;
		padding:0 !important;
	}

	.gm-style-iw-c {
		padding: 16px;
		top: 20px;

		box-shadow: var(--box-shadow-2);

        .gm-style-iw-chr {
            display: none;
        }

        .gm-style-iw-d {
            overflow: hidden !important;
        }
	}

	.gm-style-iw-tc {
		display: none;
	}
}

.gm-style-iw {
	max-height:60px !important;
	padding-top:0px !important;
	button:not(.button-quaternary) { display: none !important; }

	&:after {
		background: none;
		box-shadow:none;
	}
}

.agency-tooltip-wrapper {
	max-width: 300px;
	min-width: 120px;

	display:flex;
	flex-direction:row;
	justify-content:space-between;
	align-items:center;

	gap: 8px;

	.agency-tooltip-left-side {
		max-width: 250px;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		justify-content: space-around;
		height: 60px;
		box-sizing: border-box;
		padding-top: 10px;
		padding-bottom: 10px;

		.agency-type {
			@include H7-Regular;

			color: #6B6E73;
		}

		.agency-name {
			@include H6-Medium;

			white-space: nowrap;
			text-overflow: ellipsis;
			overflow: hidden;
			color: #242526;

			max-width: 250px;
		}

	}

	.button-quaternary {
        width: 40px;
        height: 40px;

		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;

		border-radius: 8px;
		cursor: pointer;

		svg.icon {
			width: 16px;
			height: 16px;
			pointer-events: none;

			:deep(.change-color-fill) {
				fill: #616161;
			}
		}

		@media (hover: hover) and (pointer: fine) {
			&:hover {
				background-color: #E0E0E0;
			}
		}

		&:active {
			background-color: #C7C7C7;
		}

	}
}
</style>
